import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getClientByCredentials, recordClientAccess } from '../../services/clientService';

export default function ClientLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!email || !accessCode) {
        setError('Please enter both your email and access code.');
        setLoading(false);
        return;
      }

      const client = await getClientByCredentials(email.trim(), accessCode.trim());

      if (!client) {
        setError('Invalid email or access code. Please check your credentials and try again.');
        setLoading(false);
        return;
      }

      if (!client.accessEnabled) {
        setError('Your access has been temporarily disabled. Please contact our team.');
        setLoading(false);
        return;
      }

      // Record access and store session
      await recordClientAccess(client.id);
      sessionStorage.setItem('clientSession', JSON.stringify({
        id: client.id,
        companyName: client.companyName,
        contactEmail: client.contactEmail,
        accessCode: client.accessCode,
        loginAt: new Date().toISOString(),
      }));

      navigate('/client/dashboard');
    } catch (err) {
      console.error('Client login error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <img
            src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-blue.png`}
            alt="APN Logo"
            className="h-16"
          />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Client Portal
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Access your site preview and project status
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
                placeholder="your@company.com"
              />
            </div>

            <div>
              <label htmlFor="accessCode" className="block text-sm font-medium text-gray-700">
                Access Code
              </label>
              <input
                id="accessCode"
                type="text"
                required
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                maxLength={6}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-apn-blue focus:border-apn-blue font-mono text-lg tracking-widest text-center"
                placeholder="ABC123"
              />
              <p className="mt-1 text-xs text-gray-500">
                6-character code provided after your demo
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-apn-blue hover:bg-apn-dark-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apn-blue disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                'Sign In to Portal'
              )}
            </button>
          </form>

          <div className="mt-6 border-t border-gray-200 pt-6">
            <p className="text-center text-xs text-gray-500">
              Don't have an access code?{' '}
              <Link to="/about/contact" className="text-apn-blue hover:underline font-medium">
                Contact us
              </Link>{' '}
              to schedule a demo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
